import { analytics } from "@/firebase/config";
import firebase from "@/firebase/config";

/**
 * Get the current Firebase Auth user
 */
function getCurrentUser() {
  return firebase.auth().currentUser;
}

/**
 * Set user identification in Firebase Analytics
 * Should be called when user logs in or component mounts
 */
export function setUserIdentification() {
  if (!analytics) return;
  
  try {
    const user = getCurrentUser();
    
    if (user) {
      // Set user ID (Firebase UID)
      analytics.setUserId(user.uid);
      console.log('✅ Analytics: User ID set to', user.uid);
    } else {
      // Clear user identification on logout
      analytics.setUserId(null);
      console.log('✅ Analytics: User identification cleared');
    }
  } catch (error) {
    console.error('❌ Error setting user identification:', error);
  }
}

/**
 * Get user event parameters to include in all events
 */
function getUserEventParams() {
  const params = {};
  const user = getCurrentUser();
  
  if (user) {
    params.ssi_user_id = user.uid;
  }
  
  return params;
}

/**
 * Generic event tracking function
 * @param {string} eventName - Name of the event
 * @param {object} eventParams - Additional event parameters
 */
export function trackEvent(eventName, eventParams = {}) {
  if (analytics) {
    try {
      // Merge user info with event parameters
      const userParams = getUserEventParams();
      const finalParams = { ...userParams, ...eventParams };
      
      analytics.logEvent(eventName, finalParams);
      console.log(`📊 GA Event Sent: ${eventName}`, finalParams);
    } catch (error) {
      console.error(`❌ GA Event Error [${eventName}]:`, error, eventParams);
    }
  } else {
    console.warn(`⚠️ GA not available. Event not tracked: ${eventName}`, eventParams);
  }
}

/**
 * Track navigation menu item clicks
 * @param {string} itemName - Name of the menu item
 * @param {string} itemHref - Destination URL
 * @param {string} menuType - 'sidebar' or 'mobile'
 */
export function trackNavigationClick(itemName, itemHref, menuType = 'sidebar') {
  console.log(`🔵 GA Track: Navigation Click [${menuType}] - "${itemName}" → ${itemHref}`);
  trackEvent('menu_navigation_click', {
    ssi_menu_item: itemName,
    ssi_destination: itemHref,
    ssi_menu_type: menuType
  });
}

/**
 * Track dark mode toggle
 * @param {string} mode - 'dark' or 'light'
 * @param {string} menuType - 'sidebar' or 'mobile'
 */
export function trackDarkModeToggle(mode, menuType = 'sidebar') {
  console.log(`🌓 GA Track: Dark Mode Toggle [${menuType}] - Switching to ${mode} mode`);
  trackEvent('menu_dark_mode_toggle', {
    mode: mode,
    ssi_menu_type: menuType
  });
}

/**
 * Track logout button click
 * @param {string} menuType - 'sidebar' or 'mobile'
 */
export function trackLogoutClick(menuType = 'sidebar') {
  console.log(`🚪 GA Track: Logout Click [${menuType}]`);
  trackEvent('menu_logout_click', {
    ssi_menu_type: menuType
  });
}

/**
 * Track account/profile link click
 * @param {string} menuType - 'sidebar' or 'mobile'
 */
export function trackAccountClick(menuType = 'sidebar') {
  console.log(`👤 GA Track: Account Click [${menuType}]`);
  trackEvent('menu_account_click', {
    ssi_menu_type: menuType
  });
}

/**
 * Track menu loaded event
 * @param {string} menuType - 'sidebar' or 'mobile'
 */
export function trackMenuLoaded(menuType = 'sidebar') {
  trackEvent('menu_loaded', {
    ssi_menu_type: menuType,
    timestamp: new Date().toISOString()
  });
}

/**
 * Initialize analytics for a menu component
 * Sets user identification and tracks menu load
 * @param {string} menuType - 'sidebar' or 'mobile'
 */
export function initializeMenuAnalytics(menuType = 'sidebar') {
  if (analytics) {
    console.log(`✅ ${menuType === 'sidebar' ? 'SidebarMenu' : 'MobileMenu'}: Analytics is available`);
    
    // Set user identification for Analytics
    setUserIdentification();
    
    // Track menu loaded event
    setTimeout(() => {
      trackMenuLoaded(menuType);
    }, 1000);
  } else {
    console.warn(`⚠️ ${menuType === 'sidebar' ? 'SidebarMenu' : 'MobileMenu'}: Analytics is NOT available`);
    console.warn('Check that VUE_APP_FIREBASE_MEASUREMENT_ID is set in your environment variables');
  }
}

/**
 * Track purchase button click on Purchase page
 * @param {string} purchaseType - 'bundle' or 'individual'
 * @param {string} purchaseName - Name of the purchase (e.g., 'CAAP License Bundle', 'CAAP Knowledge', etc.)
 * @param {string} pricingKey - Pricing key from PRICING object (e.g., 'full-license-bundle', 'caap-knowledge')
 * @param {number} price - Price of the purchase
 * @param {number} duration - Duration in days (30 or 90)
 */
export function trackPurchaseButtonClick(purchaseType, purchaseName, pricingKey, price, duration) {
  console.log(`🛒 GA Track: Purchase Button Click - ${purchaseName} (${purchaseType}, ${pricingKey}, ₱${price}, ${duration} days)`);
  trackEvent('purchase_button_click', {
    ssi_purchase_type: purchaseType, // 'bundle' or 'individual'
    ssi_purchase_name: purchaseName,
    ssi_pricing_key: pricingKey,
    ssi_price: price,
    ssi_duration: duration,
    ssi_duration_in_words: duration === 90 ? '3 months' : duration === 30 ? '1 month' : `${duration} days`,
    timestamp: new Date().toISOString()
  });
}

/**
 * Track completed purchase in PurchaseModal
 * @param {string} purchaseName - Name of the purchase (e.g., 'CAAP License Bundle', 'CAAP Knowledge', etc.)
 * @param {string} pricingKey - Pricing key from PRICING object (e.g., 'full-license-bundle', 'caap-knowledge')
 * @param {number} originalPrice - Original price before discount
 * @param {number} finalPrice - Final price after discount
 * @param {number} duration - Duration in days (30 or 90)
 * @param {boolean} hasCoupon - Whether a coupon was used
 * @param {string} couponCode - Coupon code if used
 * @param {number} couponDiscount - Discount amount if coupon was used
 * @param {boolean} isBundled - Whether this is a bundle purchase
 */
export function trackPurchaseCompleted(purchaseName, pricingKey, originalPrice, finalPrice, duration, hasCoupon = false, couponCode = null, couponDiscount = 0, isBundled = false) {
  const discountInfo = hasCoupon ? ` (Coupon: ${couponCode}, Discount: ₱${couponDiscount})` : '';
  console.log(`✅ GA Track: Purchase Completed - ${purchaseName} (${pricingKey}, ₱${originalPrice} → ₱${finalPrice}, ${duration} days${discountInfo})`);
  trackEvent('purchase_completed', {
    ssi_purchase_name: purchaseName,
    ssi_pricing_key: pricingKey,
    ssi_original_price: originalPrice,
    ssi_final_price: finalPrice,
    ssi_duration: duration,
    ssi_duration_in_words: duration === 90 ? '3 months' : duration === 30 ? '1 month' : `${duration} days`,
    ssi_has_coupon: hasCoupon,
    ssi_coupon_code: couponCode || '',
    ssi_coupon_discount: couponDiscount,
    ssi_is_bundled: isBundled,
    ssi_discount_amount: originalPrice - finalPrice,
    timestamp: new Date().toISOString()
  });
}

